import {containsNonStandardAminoAcids} from 'biojs/Util';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';

export type ComponentKind = 'aminoAcids' | 'smiles';

export type ComponentStatus = {kind: 'empty'} | {kind: 'ready'} | {kind: 'invalid'; reason: string};

export type InputNameError = 'blank' | 'duplicate' | 'tooLong';

export type InputStatus = 'empty' | 'incomplete' | 'ready' | 'invalid';

export interface PredictionComponent {
  id: string;
  type: ComponentKind;
  value: string;
  // Number of identical copies of this component to submit. The backend config schema has no
  // stoichiometry field, so on submit each copy is materialized as a repeated component entry
  // (see buildPredictionConfig). Only meaningful for multimer models; pinned to 1 otherwise.
  copies: number;
  status: ComponentStatus;
}

/**
 * One input row in the New prediction inputs table. Each input owns its own component list and
 * becomes a single PredictionJob (one prediction config) on submit.
 */
export interface PredictionInput {
  id: string;
  // Human-readable label for this input, persisted to PredictionJob.name on submit.
  name: string;
  // Input-level name problem, recomputed by validateAllInputs. Unlike component statuses this is
  // set-wide: 'duplicate' depends on the other inputs in the batch.
  nameError: InputNameError | null;
  components: Array<PredictionComponent>;
}

export type PredictionInputsUpdate =
  | Array<PredictionInput>
  | ((currentInputs: Array<PredictionInput>) => Array<PredictionInput>);

export interface SubmitDisabledReasonParams {
  inputs: Array<PredictionInput>;
  hasModels: boolean;
  hasSelectedModel: boolean;
  isSubmitting: boolean;
  maxAminoAcidCount: number;
  // Max components allowed per prediction for the selected model. Copies expand into repeated
  // components on submit, so the summed copies per input must not exceed this.
  maxComponentsPerInput: number;
  maxInputs: number;
  minAminoAcidCount: number;
  modelName: string;
  moleculesSupported: boolean;
}

const AMINO_ACID_REGEX = /^[ACDEFGHIKLMNPQRSTVWY]+$/;
// This is intentionally only a permissive plausibility check. The backend remains authoritative
// for SMILES grammar, valence, balanced delimiters, and CCD existence.
const SMILES_CHARSET_REGEX = /^[A-Za-z0-9@+\-[\]()=#:/\\.$%*_]+$/;
const OBVIOUS_NON_SMILES_VALUES = new Set(['N/A']);

// Mirrors PREDICTION_JOB_NAME_MAX_LENGTH in
// benchling/model_hub/persistence/prediction_job_table.py.
export const MAX_INPUT_NAME_LENGTH = 263;

let nextComponentId = 0;
let nextInputId = 0;

export function makeComponent(type: ComponentKind = 'aminoAcids'): PredictionComponent {
  nextComponentId += 1;
  return {
    id: `new-prediction-component-${nextComponentId}`,
    type,
    value: '',
    copies: 1,
    status: {kind: 'empty'},
  };
}

export function makeInput(name = ''): PredictionInput {
  nextInputId += 1;
  return {
    id: `new-prediction-input-${nextInputId}`,
    name,
    nameError: null,
    components: [makeComponent('aminoAcids')],
  };
}

/** First unused `input-N`, scanning from 1. Skips names already taken (explicit or generated). */
export function makeDefaultInputName(usedNames: ReadonlySet<string>): string {
  let index = 1;
  while (usedNames.has(`input-${index}`)) {
    index += 1;
  }
  return `input-${index}`;
}

/** Assign a default name to every input that has none. Call only at creation/import time. */
export function fillMissingInputNames(
  inputs: Array<PredictionInput>,
  initiallyUsedNames: ReadonlySet<string> = new Set()
): Array<PredictionInput> {
  const usedNames = new Set(initiallyUsedNames);
  for (const input of inputs) {
    const name = input.name.trim();
    if (name.length > 0) {
      usedNames.add(name);
    }
  }
  return inputs.map((input) => {
    if (input.name.trim().length > 0) {
      return input;
    }
    const name = makeDefaultInputName(usedNames);
    usedNames.add(name);
    return {...input, name};
  });
}

/** Row label for display/aria. Blank names get a positional stand-in so controls stay labelled. */
export function getInputLabel(input: PredictionInput, index: number): string {
  return input.name.trim() || `Unnamed input ${index + 1}`;
}

export function getComponentLabel(components: Array<PredictionComponent>, index: number): string {
  let aminoAcidCount = 0;
  let ligandCount = 0;
  for (let i = 0; i <= index; i++) {
    if (components[i].type === 'aminoAcids') {
      aminoAcidCount += 1;
    } else {
      ligandCount += 1;
    }
  }

  if (components[index].type === 'aminoAcids') {
    return `Chain ${getComponentLetter(aminoAcidCount)}`;
  }
  return `Ligand ${getComponentLetter(ligandCount)}`;
}

export function getSubmitDisabledReason({
  inputs,
  hasModels,
  hasSelectedModel,
  isSubmitting,
  maxAminoAcidCount,
  maxComponentsPerInput,
  maxInputs,
  minAminoAcidCount,
  modelName,
  moleculesSupported,
}: SubmitDisabledReasonParams): string | null {
  if (isSubmitting) {
    return 'Submitting...';
  }
  if (!hasModels) {
    return 'No models available';
  }
  if (!hasSelectedModel) {
    return 'Select a model';
  }
  if (inputs.length === 0) {
    return 'Add at least one input';
  }
  if (inputs.length > maxInputs) {
    return `${modelName} supports at most ${maxInputs} ${maxInputs === 1 ? 'input' : 'inputs'} per batch`;
  }
  const nameErrors = getInputNameErrors(inputs);
  if (nameErrors.some((error) => error === 'blank')) {
    return 'Name every input';
  }
  if (nameErrors.some((error) => error === 'duplicate')) {
    return 'Input names must be unique';
  }
  if (nameErrors.some((error) => error === 'tooLong')) {
    return `Input names must be at most ${MAX_INPUT_NAME_LENGTH} characters`;
  }
  // Copies expand into repeated components on submit; the summed copies for any input must fit the
  // model's component cap (the backend rejects lists longer than this).
  if (inputs.some((input) => getTotalCopies(input.components) > maxComponentsPerInput)) {
    return `Reduce copies: at most ${maxComponentsPerInput} components per prediction`;
  }
  const allComponents = inputs.flatMap((input) => input.components);
  if (allComponents.some((component) => normalizeValue(component).length === 0)) {
    return 'Enter a value for every component';
  }
  // Validate freshly rather than trusting the stored status, so a non-empty value with a stale
  // "empty" status (e.g. edited but not yet re-validated) can't slip through as submittable.
  if (allComponents.some((component) => validateComponent(component).status.kind === 'invalid')) {
    return 'Fix invalid components';
  }
  if (allComponents.some((component) => component.type === 'smiles') && !moleculesSupported) {
    return `${modelName} does not support ligands`;
  }
  if (
    inputs.some((input) => {
      const aminoAcidCount = getTotalAminoAcidCount(input.components);
      return aminoAcidCount < minAminoAcidCount || aminoAcidCount > maxAminoAcidCount;
    })
  ) {
    return `Protein sequences must be between ${minAminoAcidCount} and ${maxAminoAcidCount} amino acids`;
  }
  return null;
}

/** Total residues submitted for an input, including repeated copies of amino acid components. */
function getTotalAminoAcidCount(components: Array<PredictionComponent>): number {
  return components.reduce(
    (total, component) =>
      component.type === 'aminoAcids'
        ? total + normalizeValue(component).length * Math.max(1, Math.floor(component.copies))
        : total,
    0
  );
}

/** Sum of copies across an input's components (each copy materializes as one component on submit). */
function getTotalCopies(components: Array<PredictionComponent>): number {
  return components.reduce((total, component) => total + Math.max(1, Math.floor(component.copies)), 0);
}

/**
 * Aggregate the per-component statuses into a single input-level status. Intended to be read
 * after validateAll has run, so component statuses reflect the latest values.
 */
export function getInputStatus(input: Pick<PredictionInput, 'components' | 'nameError'>): InputStatus {
  if (
    input.components.some((component) => component.status.kind === 'invalid') ||
    input.nameError === 'duplicate' ||
    input.nameError === 'tooLong'
  ) {
    return 'invalid';
  }
  if (input.nameError === 'blank') {
    return 'incomplete';
  }
  if (input.components.every((component) => component.status.kind === 'ready')) {
    return 'ready';
  }
  if (input.components.every((component) => component.status.kind === 'empty')) {
    return 'empty';
  }
  return 'incomplete';
}

export function normalizeInputsForModel(
  model: ScientificModelNode,
  inputs: Array<PredictionInput>
): Array<PredictionInput> {
  return inputs.map((input) => ({
    ...input,
    components: normalizeComponentsForModel(model, input.components),
  }));
}

export function normalizeComponentsForModel(
  model: ScientificModelNode,
  components: Array<PredictionComponent>
): Array<PredictionComponent> {
  if (model.capabilities.supportsMultimers) {
    return components;
  }
  const firstComponent = components[0] ?? makeComponent('aminoAcids');
  return [
    {
      ...firstComponent,
      type: 'aminoAcids',
      value: firstComponent.type === 'aminoAcids' ? firstComponent.value : '',
      // Monomer models submit a single component; copies is not meaningful, so pin it to 1.
      copies: 1,
      status: {kind: 'empty'},
    },
  ];
}

export function normalizeValue(component: PredictionComponent): string {
  if (component.type === 'aminoAcids') {
    return component.value.toUpperCase().replace(/\s/g, '');
  }
  return component.value.trim();
}

export function validateAllInputs(inputs: Array<PredictionInput>): Array<PredictionInput> {
  const nameErrors = getInputNameErrors(inputs);
  return inputs.map((input, index) => ({
    ...input,
    components: validateAll(input.components),
    nameError: nameErrors[index],
  }));
}

/**
 * Per-input name errors, positionally aligned with `inputs`. Duplicates are compared on the
 * trimmed name, case-sensitively, matching the backend.
 */
export function getInputNameErrors(inputs: Array<PredictionInput>): Array<InputNameError | null> {
  const counts = new Map<string, number>();
  for (const input of inputs) {
    const name = input.name.trim();
    if (name.length > 0) {
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  return inputs.map((input) => {
    const name = input.name.trim();
    if (name.length === 0) {
      return 'blank';
    }
    if (name.length > MAX_INPUT_NAME_LENGTH) {
      return 'tooLong';
    }
    if ((counts.get(name) ?? 0) > 1) {
      return 'duplicate';
    }
    return null;
  });
}

export function validateAll(components: Array<PredictionComponent>): Array<PredictionComponent> {
  return components.map((component) => validateComponent(component));
}

export function validateComponent(component: PredictionComponent): PredictionComponent {
  const value = normalizeValue(component);
  if (value.length === 0) {
    return {...component, value, status: {kind: 'empty'}};
  }
  if (component.type === 'smiles') {
    if (!SMILES_CHARSET_REGEX.test(value) || OBVIOUS_NON_SMILES_VALUES.has(value.toUpperCase())) {
      return {
        ...component,
        value,
        status: {
          kind: 'invalid',
          reason: "Contains characters that aren't valid in a SMILES string",
        },
      };
    }
    return {...component, value, status: {kind: 'ready'}};
  }
  if (!AMINO_ACID_REGEX.test(value) || containsNonStandardAminoAcids(value)) {
    return {
      ...component,
      value,
      status: {kind: 'invalid', reason: 'Use standard amino acid letters only'},
    };
  }
  return {...component, value, status: {kind: 'ready'}};
}

export function getComponentLetter(count: number): string {
  if (count <= 26) {
    return String.fromCharCode(64 + count);
  }
  return String(count);
}
